// @ts-ignore
import { TWEEN } from "three/examples/jsm/libs/tween.module.min";
import * as THREE from "three";
import { OrbitControls, OrbitControlsProps } from "@react-three/drei";
import { FC, MutableRefObject, useCallback, useEffect, useRef, useState, memo } from "react";
import { useFrame } from "@react-three/fiber";
import { ORBIT_CONTROLS_DEFAULTS } from "../../constants";
import getAzimuthAngleLimits from "./helpers/getAzimuthAngleLimits";
import getDistanceLimits from "./helpers/getDistanceLimits";
import { SideType } from "../../store/types/store";
import { useStoreRoom, useStoreSystem } from "../../store";
import shallow from "zustand/shallow";
import getPolarAngleLimits from "./helpers/getPolarAngleLimits";
import { fitCameraToRoom } from "./fitCameraToRoom";
import { KeyControls, moveOrbitControl } from "./helpers/moveOrbitControl";
import { PerspectiveCamera, Vector3 } from "three";

interface SmartOrbitControlsProps extends OrbitControlsProps {
    roomSizes: any;
    roomHeight: number;
    roomLength: number;
    locationSubMenu: any;
    isMoveCameraRef: MutableRefObject<boolean>;
    moveCameraEnabled: boolean;
    setIsMoveCamera: (isMove: boolean) => void;
    currentSide: SideType;
}

export const SmartOrbitControls: FC<SmartOrbitControlsProps> = memo(
    ({
        enabled,
        onChange,
        roomSizes,
        locationSubMenu,
        roomHeight,
        roomLength,
        isMoveCameraRef,
        moveCameraEnabled,
        setIsMoveCamera,
        currentSide,
        ...props
    }) => {
        // @ts-ignore
        const orbitControlsRef = useRef<Object3DNode<OrbitControls, typeof OrbitControls>>(null);
        const controlsRef = useRef<KeyControls>({});
        const [enableZoom, setEnableZoom] = useState<boolean>(false);
        const [startAnim, setStartAnim] = useState(false);

        const {
            location,
            targetCameraPosition,
            targetZoom,
            cameraPosition,
            updateLabels,
            currentEditItem,
            proMode,
            isLoading,
            isViewMode,
        } = useStoreSystem(
            (state) => ({
                location: state.location,
                targetCameraPosition: state.targetCameraPosition,
                targetZoom: state.targetZoom,
                cameraPosition: state.cameraPosition,
                updateLabels: state.updateLabels,
                currentEditItem: state.currentEditItem,
                proMode: state.proMode,
                isLoading: state.isLoading,
                isViewMode: state.isViewMode,
            }),
            shallow
        );

        useFrame(() => {
            if (orbitControlsRef.current) {
                TWEEN.update();

                moveOrbitControl(
                    controlsRef.current,
                    orbitControlsRef.current.object.position,
                    orbitControlsRef.current.target
                );
                orbitControlsRef.current?.update();
            }
        });

        useEffect(() => {
            setEnableZoom(true);

            const handlerKeydown = ({ code }: KeyboardEvent) => {
                controlsRef.current[code] = true;
            };
            const handlerKeyup = ({ code }: KeyboardEvent) => {
                controlsRef.current[code] = false;
            };
            if (useStoreSystem.getState().proMode) {
                document.addEventListener("keydown", handlerKeydown, false);
                document.addEventListener("keyup", handlerKeyup, false);
            }

            return () => {
                document.removeEventListener("keydown", handlerKeydown, false);
                document.removeEventListener("keyup", handlerKeyup, false);
            };
        }, [useStoreSystem.getState().proMode]);

        useEffect(() => {
            setStartAnim(currentEditItem?.isMove);
        }, [currentEditItem]);

        const onEnd = useCallback((e?: THREE.Event | undefined) => {
            setIsMoveCamera(false);
            setEnableZoom(true);
        }, []);

        useEffect(() => {
            const camera = orbitControlsRef.current?.object;
            const target = orbitControlsRef.current?.target;

            if (camera instanceof PerspectiveCamera && target instanceof Vector3 && location === "view" && isLoading) {
                useStoreSystem.getState().viewCameraPosition = camera.position.clone();
                useStoreSystem.getState().viewTargetCameraPosition = target.clone();
            }
        }, [location, isLoading]);

        useEffect(() => {
            if (orbitControlsRef.current) {
                const camera = orbitControlsRef.current?.object;
                const currentCameraPosition = camera.position.clone();
                const fromCameraPosition = {
                    x: currentCameraPosition?.x,
                    y: currentCameraPosition?.y,
                    z: currentCameraPosition?.z,
                };
                const currentTarget = orbitControlsRef.current?.target.clone();
                const fromTarget = { x: currentTarget?.x, y: currentTarget?.y, z: currentTarget?.z };
                const toTarget = { x: targetCameraPosition?.x, y: targetCameraPosition?.y, z: targetCameraPosition?.z };
                const correctedPosition = fitCameraToRoom(targetCameraPosition, cameraPosition, currentSide, camera);
                const toCameraPosition = {
                    x: correctedPosition.x,
                    y: correctedPosition.y,
                    z: correctedPosition.z,
                };

                const tweenTarget = new TWEEN.Tween(fromTarget)
                    .to(isViewMode ? targetCameraPosition : toTarget, 1000)
                    .easing(TWEEN.Easing.Quadratic.InOut)
                    .onStart(function () {})
                    .onUpdate(function () {
                        const { x, y, z } = fromTarget;
                        orbitControlsRef.current?.target.set(x, y, z);
                        setIsMoveCamera(true);
                    })
                    .onComplete(function () {
                        const { x, y, z } = fromTarget;
                        orbitControlsRef.current?.target.set(x, y, z);
                        setIsMoveCamera(false);

                        if (isViewMode) {
                            useStoreSystem.getState().isViewMode = false;
                        }
                    });
                tweenTarget.start();
                if (targetZoom === "wall") {
                    const tweenPosition = new TWEEN.Tween(fromCameraPosition)
                        .to(isViewMode ? cameraPosition : toCameraPosition, 1000)
                        .easing(TWEEN.Easing.Quadratic.InOut)
                        .onStart(function () {})
                        .onUpdate(function () {
                            const { x, y, z } = fromCameraPosition;
                            camera.position.set(x, y, z);
                            setIsMoveCamera(true);
                        })
                        .onComplete(function () {
                            const { x, y, z } = fromCameraPosition;
                            camera.position.set(x, y, z);
                            setIsMoveCamera(false);

                            if (isViewMode) {
                                useStoreSystem.getState().isViewMode = false;
                            }
                        });
                    tweenPosition.start();
                }
            }
            return () => {};
        }, [cameraPosition, targetCameraPosition]);

        const onStart = useCallback((e?: THREE.Event | undefined) => {
            setIsMoveCamera(true);
            if (window.__IS_MOUSE_DOWN__) {
                setEnableZoom(false);
            }
        }, []);

        const onCameraChange = (e?: THREE.Event | undefined) => {
            updateLabels();
            const camera = orbitControlsRef.current?.object;
            if (camera?.position) {
                const { x, y } = camera?.position;
                const roomHeight = useStoreRoom.getState().height / 1000;
                const roomLength = useStoreRoom.getState().length / 1000;

                if (!useStoreSystem.getState().proMode) {
                    const padding = 0.1675;
                    if (y < padding) camera.position.setY(padding);
                    if (y > roomHeight - padding) camera.position.setY(roomHeight - padding);
                    if (Math.abs(x) > (roomLength - padding) / 2) {
                        camera.position.setX((x >= 0 ? roomLength - padding : -roomLength + padding) / 2);
                    }
                }
                useStoreSystem.getState().showCeiling = y < roomHeight;
            }

            onChange?.(e);
        };

        const azimuthAngle = getAzimuthAngleLimits(currentSide);
        const distanceLimits = getDistanceLimits(currentSide, targetZoom, roomSizes);
        const polarAngleLimits = getPolarAngleLimits();

        return (
            <OrbitControls
                {...ORBIT_CONTROLS_DEFAULTS}
                ref={orbitControlsRef}
                onChange={onCameraChange}
                onEnd={onEnd}
                onStart={onStart}
                rotateSpeed={0.5}
                enableDamping={false}
                enabled={!startAnim && enabled && moveCameraEnabled}
                enableZoom={enableZoom}
                // target={targetCameraPosition}
                maxAzimuthAngle={azimuthAngle.max}
                minAzimuthAngle={azimuthAngle.min}
                maxDistance={distanceLimits.max}
                minDistance={distanceLimits.min}
                maxPolarAngle={polarAngleLimits.max}
                minPolarAngle={polarAngleLimits.min}
                enableRotate
            />
        );
    }
);
